/**
 * Where a shared chat's "continue this conversation" CTA should send the reader.
 *
 * A share can come from either product, and they have SEPARATE accounts:
 * marketplace sign-ups are `account_type='marketplace'`, so a platform session
 * reads as logged-out in the marketplace and vice-versa (see
 * MarketplaceAuthContext). Offering platform login on a marketplace share was a
 * dead end — the reader signs in and still cannot open the destination, because
 * the platform chat route is gated on platform auth they don't have.
 *
 * The backend tells us which product the chat came from (`origin`, resolved from
 * the session's marketplace study binding); this maps that to the right words and
 * the right route.
 */

export const ORIGIN_MARKETPLACE = 'marketplace';

/** Marketplace login lives inside the gated study flow, not at /auth. */
const MARKETPLACE_LOGIN_PATH = '/marketplace/run';
const PLATFORM_LOGIN_PATH = '/auth';
/** Fallback when the share doesn't name its study — better than a dead study URL. */
const MARKETPLACE_HOME_PATH = '/marketplace';

/** The marketplace entry, where a reader starts their OWN study. */
const MARKETPLACE_RUN_PATH = '/marketplace/run';

const MARKETPLACE_NEW_STUDY_LABEL = 'Run a new study';
const PLATFORM_SIGN_IN_LABEL = 'Sign in to Continue';
const CONTINUE_LABEL = 'Continue Chat';

const MARKETPLACE_HEADING = 'Want to run your own study?';
const CONTINUE_HEADING = 'Want to continue this conversation?';

const MARKETPLACE_PROMPT = 'Run your own study with these AI Consumer Twins.';
const PLATFORM_PROMPT = 'Continue this conversation in your own account.';
// Already signed in — there is nothing to name, so don't mention either product.
const SIGNED_IN_PROMPT = 'Continue this conversation in your own account.';

export const isMarketplaceShare = (origin?: string | null): boolean =>
  origin === ORIGIN_MARKETPLACE;

/**
 * Whether the person READING the share holds a platform account.
 *
 * The share's origin says where the chat came from; it says nothing about who opened
 * the link. An internal platform account (admins can fork any share) legitimately
 * belongs in the enterprise app, so redirecting them into a marketplace study — which
 * their session reads as logged-out in, and which belongs to the SHARER anyway — takes
 * away a working fork. Their account type is what decides, not the share's.
 */
export const isPlatformReader = (
  isAuthenticated: boolean,
  accountType?: string | null,
): boolean => isAuthenticated && accountType !== ORIGIN_MARKETPLACE;

/**
 * The button copy for an anonymous reader.
 *
 * A marketplace share does not offer a continuation at all (see continueLabel), so it
 * must not promise one before sign-in either — it offers a NEW study, which is what
 * /marketplace/run actually delivers.
 */
export const signInLabel = (origin?: string | null): string =>
  isMarketplaceShare(origin) ? MARKETPLACE_NEW_STUDY_LABEL : PLATFORM_SIGN_IN_LABEL;

/**
 * The action offered to a SIGNED-IN reader.
 *
 * "Continue Chat" is only honest when the reader can actually continue THIS chat:
 *  - a platform reader forks it into their own session, and
 *  - the marketplace OWNER re-opens their own study.
 * Any other marketplace reader cannot. The chat lives under the sharer's study, and
 * every marketplace study route is scoped by owner (get_study_for_user), so following
 * it 404s. Even if it opened, the query budget is per-study and was paid for by the
 * sharer — continuing would spend someone else's queries. They get a new study.
 */
export const continueLabel = (
  origin: string | null | undefined,
  isOwner = false,
  platformReader = false,
): string =>
  isMarketplaceShare(origin) && !isOwner && !platformReader
    ? MARKETPLACE_NEW_STUDY_LABEL
    : CONTINUE_LABEL;

/**
 * The heading on the CTA card. Sits directly above continuePrompt, so it has to move
 * with it — leaving "Want to continue this conversation?" over a "Run a new study"
 * button reproduces the mismatch this module already exists to prevent.
 */
export const continueHeading = (
  origin: string | null | undefined,
  isOwner = false,
  platformReader = false,
): string =>
  isMarketplaceShare(origin) && !isOwner && !platformReader
    ? MARKETPLACE_HEADING
    : CONTINUE_HEADING;

/**
 * The supporting line above that button. Must not promise a continuation the reader
 * cannot have: a marketplace reader who does not own the chat is being offered a new
 * study, signed in or not.
 */
export const continuePrompt = (
  origin: string | null | undefined,
  isAuthenticated: boolean,
  isOwner = false,
  platformReader = false,
): string => {
  if (isMarketplaceShare(origin) && !isOwner && !platformReader) return MARKETPLACE_PROMPT;
  return isAuthenticated ? SIGNED_IN_PROMPT : PLATFORM_PROMPT;
};

/**
 * Where an anonymous reader goes to sign in.
 *
 * The platform path round-trips back here with `fork=true` so the fork happens
 * automatically after login. The marketplace has no such return hop: its gate
 * renders in place (it never redirects), and a marketplace account cannot fork
 * into a platform chat session anyway — so it lands on the marketplace entry
 * rather than promising a continuation that would 403.
 */
export const signInPath = (origin: string | null | undefined, shareToken?: string): string =>
  isMarketplaceShare(origin)
    ? MARKETPLACE_LOGIN_PATH
    : `${PLATFORM_LOGIN_PATH}?redirect=/chat/shared/${shareToken}?fork=true`;

/**
 * Whether to render the enterprise left nav around the shared view.
 *
 * Never on a marketplace share: every link in it is a platform route that
 * ProtectedRoute bounces a marketplace account off, so it is chrome for an app the
 * reader cannot enter. An anonymous reader of a marketplace share is in the same
 * position — the share is the whole page they were sent.
 *
 * A signed-in PLATFORM reader is the exception: the nav works for them, so hiding it on
 * a marketplace share would take away navigation they can actually use.
 */
export const showsEnterpriseNav = (origin?: string | null, platformReader = false): boolean =>
  !isMarketplaceShare(origin) || platformReader;

/**
 * Where the header logo goes.
 *
 * `/` is ProtectedRoute'd, so an ANONYMOUS marketplace reader clicking the logo lands on
 * the enterprise /auth form — the same dead end the sign-in CTA was fixed for. Send a
 * marketplace share's reader to the marketplace instead.
 */
export const homePath = (origin?: string | null, platformReader = false): string =>
  isMarketplaceShare(origin) && !platformReader ? MARKETPLACE_HOME_PATH : '/';

/**
 * Where a SIGNED-IN reader continues a marketplace conversation.
 *
 * Not the platform fork: that creates an /ask-natural-organic/{id} session, and a
 * marketplace account is bounced off that route (ProtectedRoute, mirroring the
 * backend guard) — which is the "it redirects to home page" report. A marketplace
 * conversation lives under its study instead.
 *
 * Returns null for a platform share, which has no fixed destination: it POSTs the
 * fork and navigates to the NEW session id the response returns. Also null for a
 * PLATFORM reader of a marketplace share (see isPlatformReader) — they keep the fork
 * they already had.
 */
export const continuePath = (
  origin: string | null | undefined,
  marketplaceStudyId: number | null | undefined,
  platformReader = false,
  isOwner = false,
): string | null => {
  if (!isMarketplaceShare(origin) || platformReader) return null;
  // Only the OWNER can open the study this chat lives under. Every marketplace study
  // route resolves via get_study_for_user, so sending anyone else there returned
  // 404 "Chat study not found" and rendered an error screen — the share's CTA was a
  // dead end for exactly the people shares are sent to. They start their own study.
  if (!isOwner) return MARKETPLACE_RUN_PATH;
  return marketplaceStudyId
    ? `/marketplace/studies/${marketplaceStudyId}/chat`
    : MARKETPLACE_HOME_PATH;
};
